<template>
  <div class="owner-batch-create-page">
    <a-card :bordered="false">
      <template #title>
        <div class="card-title-row">
          <span>批量建立业主信息</span>
          <a-button icon="arrow-left" @click="goBack">返回</a-button>
        </div>
      </template>

      <a-alert
        v-if="communityName"
        type="info"
        show-icon
        class="mb-3"
        :message="`当前导入所属小区：${communityName}`"
      />

      <!-- 收款账号 -->
      <a-form-model layout="vertical" class="mb-4">
        <a-form-model-item label="收款账号（可选）">
          <a-select
            v-model="payAccountId"
            placeholder="请选择收款账号"
            style="width: 100%; max-width: 480px"
            :loading="payAccountsLoading"
            allowClear
          >
            <a-select-option
              v-for="a in payAccounts"
              :key="a.id"
              :value="a.id"
              :disabled="a.active === false"
            >
              {{ a.bank_account_name }}｜{{ a.bank_account_no }}｜{{ a.bank_name }}
              <span v-if="a.active === false" style="color:#999">（停用）</span>
            </a-select-option>
          </a-select>
          <div class="muted" v-if="payAccounts.length === 0 && !payAccountsLoading">
            暂无可用收款账号（收款账号可不选择）。
          </div>
        </a-form-model-item>

        <!-- 同步生成水表 / 电表 -->
        <a-form-model-item label="同步生成">
          <a-checkbox v-model="syncWater">同步生成水表</a-checkbox>
          <a-checkbox v-model="syncElectric" class="ml-3">同步生成电表</a-checkbox>
        </a-form-model-item>

        <!-- 水表：同时建立主表 或 选择上级表（仅勾选「同步生成水表」时可选，避免孤立总表） -->
        <a-form-model-item label="水表上级">
          <a-radio-group v-model="waterParentMode" class="mr-3" :disabled="!syncWater">
            <a-radio :value="'none'">一级表（无上级）</a-radio>
            <a-radio :value="'main'">同时建立主水表</a-radio>
            <a-radio :value="'select'">选择上级表</a-radio>
          </a-radio-group>
          <a-select
            v-if="waterParentMode === 'select'"
            v-model="parentWaterMeterId"
            placeholder="模糊搜索表名"
            style="width: 320px"
            :disabled="!syncWater"
            :filter-option="false"
            :not-found-content="waterMeterSearching ? undefined : null"
            show-search
            allowClear
            @search="onSearchWaterMeter"
          >
            <a-select-option v-for="m in waterMeterOptions" :key="m.id" :value="m.id">
              {{ m.room_label }}（{{ m.serial_no }}）
            </a-select-option>
          </a-select>
          <template v-if="waterParentMode === 'main'">
            <a-input-number
              v-model="mainWaterInitial"
              placeholder="主水表初始读数，空为0"
              :min="0"
              :precision="2"
              style="width: 200px; margin-left: 8px"
            />
          </template>
          <span v-if="!syncWater" class="muted">请先勾选「同步生成水表」</span>
        </a-form-model-item>

        <!-- 电表：同时建立主表 或 选择上级表（仅勾选「同步生成电表」时可选） -->
        <a-form-model-item label="电表上级">
          <a-radio-group v-model="electricParentMode" class="mr-3" :disabled="!syncElectric">
            <a-radio :value="'none'">一级表（无上级）</a-radio>
            <a-radio :value="'main'">同时建立主电表</a-radio>
            <a-radio :value="'select'">选择上级表</a-radio>
          </a-radio-group>
          <a-select
            v-if="electricParentMode === 'select'"
            v-model="parentElectricMeterId"
            placeholder="模糊搜索表名"
            style="width: 320px"
            :disabled="!syncElectric"
            :filter-option="false"
            :not-found-content="electricMeterSearching ? undefined : null"
            show-search
            allowClear
            @search="onSearchElectricMeter"
          >
            <a-select-option v-for="m in electricMeterOptions" :key="m.id" :value="m.id">
              {{ m.room_label }}（{{ m.serial_no }}）
            </a-select-option>
          </a-select>
          <template v-if="electricParentMode === 'main'">
            <a-input-number
              v-model="mainElectricInitial"
              placeholder="主电表初始读数，空为0"
              :min="0"
              :precision="2"
              style="width: 200px; margin-left: 8px"
            />
          </template>
          <span v-if="!syncElectric" class="muted">请先勾选「同步生成电表」</span>
        </a-form-model-item>

        <!-- 上传 Excel -->
        <a-form-model-item label="上传 Excel">
          <a-upload
            :before-upload="beforeUpload"
            :show-upload-list="false"
            accept=".xlsx,.xls"
          >
            <a-button icon="upload">选择文件</a-button>
          </a-upload>
          <span class="ml-2 muted" v-if="parsedRows.length">已解析 {{ parsedRows.length }} 行</span>
        </a-form-model-item>

        <a-form-model-item>
          <a-button
            type="primary"
            :loading="submitting"
          :disabled="!parsedRows.length"
            @click="submitBatch"
          >
            开始批量建立
          </a-button>
        </a-form-model-item>
      </a-form-model>

      <!-- 结果 -->
      <a-alert
        v-if="result"
        :type="result.failures && result.failures.length ? 'warning' : 'success'"
        show-icon
        class="mb-3"
        :message="`共 ${result.total} 行，成功 ${result.success_count} 行，失败 ${(result.failures || []).length} 行`"
      />
      <a-table
        v-if="result && result.failures && result.failures.length"
        :columns="failureColumns"
        :data-source="result.failures"
        row-key="row_index"
        :pagination="{ pageSize: 10 }"
        size="small"
      />
    </a-card>
  </div>
</template>

<script>
import request from "@/utils/request";
import * as XLSX from "xlsx";

const BATCH_HEADER_ALIAS = {
  building_no: ["楼号", "building_no"],
  unit_no: ["房号", "unit_no"],
  owner_name: ["业主姓名", "owner_name"],
  contact_phone: ["手机号码", "contact_phone"],
  floor_area: ["房屋面积(㎡)", "房屋面积", "floor_area"],
  reminder_days: ["催款提前天数", "reminder_days"],
  water_initial: ["水表初始读数", "water_initial"],
  electric_initial: ["电表初始读数", "electric_initial"],
  water_label: ["水表表标签名", "water_label"],
  water_meter_type: ["水表表计类型", "water_meter_type"],
  water_multiplier: ["水表表计倍率", "water_multiplier"],
  electric_label: ["电表表标签名", "electric_label"],
  electric_meter_type: ["表计类型", "电表表计类型", "electric_meter_type"],
  electric_multiplier: ["表计倍率", "电表表计倍率", "electric_multiplier"],
};

export default {
  name: "OwnerBatchCreate",
  data() {
    return {
      teamId: null,
      communityName: "",
      payAccountsLoading: false,
      payAccounts: [],
      payAccountId: null,
      syncWater: true,
      syncElectric: true,
      waterParentMode: "none",
      electricParentMode: "none",
      mainWaterInitial: null,
      mainElectricInitial: null,
      parentWaterMeterId: null,
      parentElectricMeterId: null,
      waterMeterOptions: [],
      electricMeterOptions: [],
      waterMeterSearching: false,
      electricMeterSearching: false,
      parsedRows: [],
      submitting: false,
      result: null,
      failureColumns: [
        { title: "行号", dataIndex: "row_index", width: 80 },
        { title: "楼号", dataIndex: "building_no" },
        { title: "房号", dataIndex: "unit_no" },
        { title: "原因", dataIndex: "reason", ellipsis: true },
      ],
    };
  },
  watch: {
    syncWater(val) {
      if (!val) {
        this.waterParentMode = "none";
        this.parentWaterMeterId = null;
      }
    },
    syncElectric(val) {
      if (!val) {
        this.electricParentMode = "none";
        this.parentElectricMeterId = null;
      }
    },
  },
  created() {
    this.fetchMyTeam();
    this.fetchPayAccounts();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "OwnerQuery" });
    },
    async fetchMyTeam() {
      try {
        const res = await request({ url: "/manage_data/my_team/", method: "get" });
        const data = (res && res.data) ? res.data : res;
        this.teamId = data.team_id;
        this.communityName = data.community_name || "";
      } catch (e) {
        console.error(e);
        this.teamId = null;
        this.communityName = "";
        this.$message.error("获取当前用户所属小区失败");
      }
    },
    async fetchPayAccounts() {
      this.payAccountsLoading = true;
      try {
        const res = await request({
          url: "/manage_data/pay_accounts/",
          method: "get",
          params: { active: true },
        });
        const data = (res && res.data) ? res.data : res;
        const list = Array.isArray(data) ? data : (data.results || []);
        this.payAccounts = list;
        if (list.length && !this.payAccountId) {
          const first = list.find((x) => x.active !== false);
          if (first) this.payAccountId = first.id;
        }
      } catch (e) {
        console.error(e);
        this.payAccounts = [];
      } finally {
        this.payAccountsLoading = false;
      }
    },
    getCell(row, headerMap, aliases) {
      for (const a of aliases) {
        const idx = headerMap[this.normalizeHeader(a)];
        if (idx != null && row[idx] !== undefined) return row[idx];
      }
      return undefined;
    },
    normalizeHeader(h) {
      return String(h || "")
        .replace(/\s+/g, "")
        .replace(/（/g, "(")
        .replace(/）/g, ")")
        .trim();
    },
    beforeUpload(file) {
      const isExcel =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      if (!isExcel) {
        this.$message.error("请上传 .xls 或 .xlsx 文件");
        return false;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });
          if (!jsonData || jsonData.length < 2) {
            this.$message.warning("表头+至少一行数据");
            this.parsedRows = [];
            return;
          }
          const headerRow = jsonData[0];
          const headerMap = {};
          (headerRow || []).forEach((h, idx) => {
            const k = this.normalizeHeader(h);
            if (k && headerMap[k] == null) headerMap[k] = idx;
          });
          const hasBuilding = (headerMap["楼号"] != null) || (headerMap["building_no"] != null);
          const hasUnit = (headerMap["房号"] != null) || (headerMap["unit_no"] != null);
          if (!hasBuilding || !hasUnit) {
            this.$message.error("文件格式错误：缺少必填列（楼号、房号），请使用批量建立业主信息模板");
            this.parsedRows = [];
            return;
          }
          const MAX_ROWS = 500;
          if (jsonData.length - 1 > MAX_ROWS) {
            this.$message.error(`单次导入不得超过 ${MAX_ROWS} 行`);
            this.parsedRows = [];
            return;
          }
          const rows = [];
          const toNum = (v) => {
            if (v == null || v === "") return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
          };
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            const building_no = this.getCell(row || [], headerMap, ["楼号", "building_no"]);
            const unit_no = this.getCell(row || [], headerMap, ["房号", "unit_no"]);
            const owner_name = this.getCell(row || [], headerMap, ["业主姓名", "owner_name"]);
            const contact_phone = this.getCell(row || [], headerMap, ["手机号码", "contact_phone"]);
            const floor_area = this.getCell(row || [], headerMap, ["房屋面积(㎡)", "房屋面积", "floor_area"]);
            const reminder_days = this.getCell(row || [], headerMap, ["催款提前天数", "reminder_days"]);
            const water_initial = this.getCell(row || [], headerMap, ["水表初始读数", "water_initial"]);
            const electric_initial = this.getCell(row || [], headerMap, ["电表初始读数", "electric_initial"]);
            const water_label = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.water_label);
            const water_meter_type = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.water_meter_type);
            const water_multiplier = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.water_multiplier);
            const electric_label = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.electric_label);
            const electric_meter_type = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.electric_meter_type);
            const electric_multiplier = this.getCell(row || [], headerMap, BATCH_HEADER_ALIAS.electric_multiplier);
            // 不跳过空行，全部发送给后端，以便失败列表中的行号与 Excel 行号一致
            rows.push({
              building_no: String(building_no ?? "").trim(),
              unit_no: String(unit_no ?? "").trim(),
              owner_name: owner_name != null ? String(owner_name).trim() : "",
              contact_phone: contact_phone != null ? String(contact_phone).trim() : "",
              floor_area: toNum(floor_area),
              reminder_days: toNum(reminder_days),
              water_initial: toNum(water_initial),
              electric_initial: toNum(electric_initial),
              water_label: water_label != null ? String(water_label).trim() : "",
              water_meter_type: water_meter_type != null ? String(water_meter_type).trim() : "",
              water_multiplier: toNum(water_multiplier),
              electric_label: electric_label != null ? String(electric_label).trim() : "",
              electric_meter_type: electric_meter_type != null ? String(electric_meter_type).trim() : "",
              electric_multiplier: toNum(electric_multiplier),
            });
          }
          this.parsedRows = rows;
          this.result = null;
          this.$message.success(`已解析 ${rows.length} 行`);
        } catch (err) {
          console.error(err);
          this.$message.error("解析 Excel 失败");
          this.parsedRows = [];
        }
      };
      reader.readAsBinaryString(file);
      return false;
    },
    async onSearchWaterMeter(q) {
      if (!q || !this.teamId) {
        this.waterMeterOptions = [];
        return;
      }
      this.waterMeterSearching = true;
      try {
        const res = await request({
          url: "/electricity/meters/",
          method: "get",
          params: { search: q, kind: "water" },
        });
        const data = (res && res.data) ? res.data : res;
        this.waterMeterOptions = Array.isArray(data) ? data : (data.results || []);
      } catch (e) {
        this.waterMeterOptions = [];
      } finally {
        this.waterMeterSearching = false;
      }
    },
    async onSearchElectricMeter(q) {
      if (!q || !this.teamId) {
        this.electricMeterOptions = [];
        return;
      }
      this.electricMeterSearching = true;
      try {
        const res = await request({
          url: "/electricity/meters/",
          method: "get",
          params: { search: q, kind: "electric" },
        });
        const data = (res && res.data) ? res.data : res;
        this.electricMeterOptions = Array.isArray(data) ? data : (data.results || []);
      } catch (e) {
        this.electricMeterOptions = [];
      } finally {
        this.electricMeterSearching = false;
      }
    },
    parseMainInitial(val) {
      if (val == null || val === "") return 0;
      const n = Number(val);
      if (Number.isNaN(n) || n < 0) return null;
      return n;
    },
    async submitBatch() {
      if (!this.teamId) {
        this.$message.warning("无法获取当前用户 team_id");
        return;
      }
      if (!this.parsedRows.length) {
        this.$message.warning("请先上传并解析 Excel");
        return;
      }
      const mainWaterInitial = this.waterParentMode === "main" ? this.parseMainInitial(this.mainWaterInitial) : null;
      const mainElectricInitial = this.electricParentMode === "main" ? this.parseMainInitial(this.mainElectricInitial) : null;
      if (this.waterParentMode === "main" && mainWaterInitial === null) {
        this.$message.warning("主水表初始读数不能为负数");
        return;
      }
      if (this.electricParentMode === "main" && mainElectricInitial === null) {
        this.$message.warning("主电表初始读数不能为负数");
        return;
      }
      this.submitting = true;
      this.result = null;
      try {
        const res = await request({
          url: "/electricity/accounts/batch-import/",
          method: "post",
          data: {
            rows: this.parsedRows,
            pay_account_id: this.payAccountId || null,
            sync_water: this.syncWater,
            sync_electric: this.syncElectric,
            create_main_water: this.waterParentMode === "main",
            create_main_electric: this.electricParentMode === "main",
            main_water_initial: this.waterParentMode === "main" ? (mainWaterInitial ?? 0) : undefined,
            main_electric_initial: this.electricParentMode === "main" ? (mainElectricInitial ?? 0) : undefined,
            parent_water_meter_id: this.waterParentMode === "select" ? this.parentWaterMeterId : null,
            parent_electric_meter_id: this.electricParentMode === "select" ? this.parentElectricMeterId : null,
          },
        });
        const data = (res && res.data) ? res.data : res;
        this.result = data;
        this.$message.success(`导入完成：成功 ${data.success_count} 行，失败 ${(data.failures || []).length} 行`);
      } catch (e) {
        console.error(e);
        const msg = e.response?.data?.detail || e.response?.data?.error || "批量建立失败";
        this.$message.error(msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.owner-batch-create-page {
  padding: 16px;
}
.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.mb-3 {
  margin-bottom: 12px;
}
.mb-4 {
  margin-bottom: 16px;
}
.ml-2 {
  margin-left: 8px;
}
.ml-3 {
  margin-left: 12px;
}
.muted {
  color: #999;
  font-size: 12px;
}
</style>
